import { useEffect, useState, type CSSProperties, type SyntheticEvent } from 'react';
import { initials } from '@/lib/format';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

function hueStyle(hue: number | null | undefined): CSSProperties {
  return { '--h': String(hue ?? 210) } as CSSProperties;
}

/** A round avatar with initials, tinted by the person's hue. Pass `title` to name it on hover. */
export function Avatar({
  name,
  hue,
  size,
  title,
  className,
  decorative = true,
}: {
  name: string;
  hue?: number | null;
  size?: AvatarSize;
  title?: string;
  className?: string;
  /** Avatars sit next to the name almost everywhere, so they are hidden from screen readers by default. */
  decorative?: boolean;
}) {
  return (
    <span
      className={['av', size, className].filter(Boolean).join(' ')}
      style={hueStyle(hue)}
      title={title}
      aria-hidden={decorative ? true : undefined}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : name}
    >
      {initials(name)}
    </span>
  );
}

/**
 * How a logo sits in its tile: `full` fills the tile (a square picture with a solid background, like an app icon),
 * `light` and `dark` sit inside it on white or on a dark tile (a dark tile for light pictures on transparency).
 */
type LogoFit = 'full' | 'light' | 'dark';

/** Fits already measured and logos that did not load, so each picture is looked at once per visit. */
const fits = new Map<string, LogoFit>();
const broken = new Set<string>();

export function logoUrl(logo: string): string {
  return `/api/logos/${logo}`;
}

/** Reads a few pixels of the loaded picture: its edges say whether it is a solid square, its colours how bright it is. */
function measureFit(img: HTMLImageElement): LogoFit {
  const w = img.naturalWidth || 1;
  const h = img.naturalHeight || 1;
  const square = Math.abs(w / h - 1) < 0.08;
  try {
    const n = 32;
    const canvas = document.createElement('canvas');
    canvas.width = n;
    canvas.height = n;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return 'light';
    ctx.drawImage(img, 0, 0, n, n);
    const px = ctx.getImageData(0, 0, n, n).data;
    const alpha = (x: number, y: number) => px[(y * n + x) * 4 + 3]!;
    const m = Math.floor(n / 2);
    const k = Math.round(n * 0.1);
    // The middle of each edge and a point a little in from each corner: all solid means a filled square, rounded or not.
    const probes: [number, number][] = [[m, 0], [0, m], [n - 1, m], [m, n - 1], [k, k], [n - 1 - k, k], [k, n - 1 - k], [n - 1 - k, n - 1 - k]];
    if (square && probes.every(([x, y]) => alpha(x, y) > 235)) return 'full';
    let weight = 0;
    let light = 0;
    for (let i = 0; i < px.length; i += 4) {
      const a = px[i + 3]! / 255;
      if (a < 0.5) continue;
      light += a * ((0.2126 * px[i]! + 0.7152 * px[i + 1]! + 0.0722 * px[i + 2]!) / 255);
      weight += a;
    }
    return weight > 0 && light / weight > 0.86 ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/**
 * A company's tile: its logo when Clientbook found one on the company's website, else a rounded square with one or
 * two letters. The letters show while the logo loads and stay when it cannot load.
 */
export function CompanyMono({
  name,
  hue,
  size,
  className,
  logo,
}: {
  name: string;
  hue?: number | null;
  size?: AvatarSize;
  className?: string;
  logo?: string | null;
}) {
  const usable = logo && !broken.has(logo) ? logo : null;
  const [fit, setFit] = useState<LogoFit | null>(() => (usable ? (fits.get(usable) ?? null) : null));
  const [failed, setFailed] = useState(false);
  /** Fades in only the first time the picture shows, not each time a known logo appears again. */
  const [fresh, setFresh] = useState(false);
  useEffect(() => {
    setFit(usable ? (fits.get(usable) ?? null) : null);
    setFailed(false);
  }, [usable]);

  if (!usable || failed) {
    return (
      <span className={['mono-co', size, className].filter(Boolean).join(' ')} style={hueStyle(hue)} aria-hidden="true">
        {monogram(name)}
      </span>
    );
  }
  const onLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    if (fit) return;
    const next = fits.get(usable) ?? measureFit(e.currentTarget);
    fits.set(usable, next);
    setFresh(true);
    setFit(next);
  };
  const onError = () => {
    broken.add(usable);
    setFailed(true);
  };
  return (
    <span
      className={['mono-co', fit ? `has-logo logo-${fit}` : 'logo-wait', fresh ? 'logo-fresh' : null, size, className].filter(Boolean).join(' ')}
      style={hueStyle(hue)}
      aria-hidden="true"
    >
      {fit ? null : monogram(name)}
      <img src={logoUrl(usable)} alt="" decoding="async" draggable={false} onLoad={onLoad} onError={onError} />
    </span>
  );
}

/** "Arcadia Health" → "AH", "Kestrel" → "KE". */
export function monogram(name: string): string {
  const words = name
    .replace(/\s+(inc|ltd|llc|gmbh|ag|bv|ab|as|sa|plc|co)\.?$/i, '')
    .split(/[\s\-&]+/)
    .filter((w) => /[\p{L}\p{N}]/u.test(w));
  if (words.length === 0) return '?';
  const first = [...words[0]!];
  if (words.length === 1) return ((first[0] ?? '') + (first[1] ?? '')).toUpperCase();
  return ((first[0] ?? '') + ([...words[1]!][0] ?? '')).toUpperCase();
}

/** Overlapping avatars for a few people, with the rest summarised. */
export function AvatarStack({ people, size = 'sm', max = 4 }: { people: { name: string; hue?: number | null }[]; size?: AvatarSize; max?: number }) {
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;
  return (
    <span className="av-stack" aria-label={people.map((p) => p.name).join(', ')} role="img">
      {shown.map((p, i) => (
        <Avatar key={i} name={p.name} hue={p.hue} size={size} title={p.name} />
      ))}
      {rest > 0 ? (
        <span className={['av', size].join(' ')} style={{ '--h': '220' } as CSSProperties} aria-hidden="true">
          +{rest}
        </span>
      ) : null}
    </span>
  );
}
